import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  query,
  where,
  orderBy,
  Timestamp,
  runTransaction
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { Mission, UserMissionProgress } from '../types';
import { Capacitor } from '@capacitor/core';
import { firestoreRestApi } from '../utils/firestoreRestApi';

export const missionService = {
  // Create a new mission
  async createMission(mission: Omit<Mission, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> {
    try {
      const missionsCollection = collection(db, 'missions');
      const missionData = {
        ...mission,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now()
      };
      
      const docRef = await addDoc(missionsCollection, missionData);
      return docRef.id;
    } catch (error) {
      console.error('Error creating mission:', error);
      throw error;
    }
  },

  // Get active missions for a team
  async getActiveMissions(teamId: string): Promise<Mission[]> {
    try {
      // On iOS, use REST API workaround
      if (Capacitor.getPlatform() === 'ios') {
        const documents = await firestoreRestApi.queryDocuments(
          'missions',
          [
            { field: 'teamId', op: '==', value: teamId },
            { field: 'isActive', op: '==', value: true }
          ]
        );
        
        // Sort by createdAt descending (client-side)
        const sorted = documents.sort((a, b) => {
          const aTime = a.data().createdAt?.seconds || 0;
          const bTime = b.data().createdAt?.seconds || 0;
          return bTime - aTime;
        });
        
        return sorted.map(doc => ({
          id: doc.id,
          ...doc.data()
        })) as Mission[];
      }
      
      // On web/Android, use SDK
      const missionsCollection = collection(db, 'missions');
      const q = query(
        missionsCollection,
        where('teamId', '==', teamId),
        where('isActive', '==', true),
        orderBy('createdAt', 'desc')
      );
      
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Mission[];
    } catch (error) {
      console.error('Error getting active missions:', error);
      throw error;
    }
  },

  // Get user's progress on a specific mission
  async getUserMissionProgress(userId: string, missionId: string): Promise<UserMissionProgress | null> {
    try {
      // On iOS, use REST API workaround
      if (Capacitor.getPlatform() === 'ios') {
        const progressDoc = await firestoreRestApi.getDocument('userMissionProgress', `${userId}_${missionId}`);
        if (!progressDoc) return null;
        
        return {
          id: progressDoc.id,
          ...progressDoc.data()
        } as UserMissionProgress;
      }
      
      // On web/Android, use SDK
      const progressRef = doc(db, 'userMissionProgress', `${userId}_${missionId}`);
      const progressDoc = await getDoc(progressRef);
      
      if (!progressDoc.exists()) {
        return null;
      }
      
      return {
        id: progressDoc.id,
        ...progressDoc.data()
      } as UserMissionProgress;
    } catch (error) {
      console.error('Error getting user mission progress:', error);
      throw error;
    }
  },

  // Update user's progress on a mission
  async updateMissionProgress(
    userId: string,
    missionId: string,
    teamId: string,
    progress: number,
    isCompleted: boolean = false
  ): Promise<void> {
    try {
      const progressRef = doc(db, 'userMissionProgress', `${userId}_${missionId}`);
      
      const progressData: Partial<UserMissionProgress> = {
        progress,
        isCompleted,
        updatedAt: Timestamp.now()
      };
      
      if (isCompleted) {
        progressData.completedAt = Timestamp.now();
        progressData.pointsEarned = 10; // Mission completion reward
      }
      
      await updateDoc(progressRef, progressData);
      
      // Award points if completed
      if (isCompleted) {
        const { pointsService } = await import('./pointsService');
        await pointsService.awardMissionPoints(userId, teamId, 10, missionId);
      }
    } catch (error) {
      console.error('Error updating mission progress:', error);
      throw error;
    }
  },

  // Get all user's mission progress for a team
  async getUserMissionsProgress(userId: string, teamId: string): Promise<UserMissionProgress[]> {
    try {
      // On iOS, use REST API workaround
      if (Capacitor.getPlatform() === 'ios') {
        const documents = await firestoreRestApi.queryDocuments(
          'userMissionProgress',
          [
            { field: 'userId', op: '==', value: userId },
            { field: 'teamId', op: '==', value: teamId }
          ]
        );
        
        return documents.map(doc => ({
          id: doc.id,
          ...doc.data()
        })) as UserMissionProgress[];
      }
      
      // On web/Android, use SDK
      const progressCollection = collection(db, 'userMissionProgress');
      const q = query(
        progressCollection,
        where('userId', '==', userId),
        where('teamId', '==', teamId)
      );
      
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as UserMissionProgress[];
    } catch (error) {
      console.error('Error getting user missions progress:', error);
      throw error;
    }
  }
};
